import { useCallback, useEffect, useRef, useState } from "react";

import { CONTROL_VISIBILITY_TIMEOUTS } from "../../constants";
import * as Enums from "../../enums";
import { usePlayerStore } from "../../store/usePlayerStore";

interface VisibilityToggleFunctions {
  toggleControls: (showControls: boolean, hidingTimeout?: number) => void;
  toggleMobileControls: (
    showControls?: boolean | undefined,
    hidingTimeout?: number
  ) => void;
}

/**
 * Controls visibility rules:
 *
 * - Controls are visible only if Ui type is "default" (i.e. controls never visible background video)
 * - Controls are always visible if settings menu is open
 * - Controls are visible, if player is focused
 *
 * On pause:
 * - Desktop: Controls always visible
 * - Mobile: Controls are shown after pause, but user can hide/show them by single tap
 *
 * On playback start:
 * - Desktop: controls are visible always
 * - Mobile: controls are visible only if embed parameters autoControls=true or autoplay=true
 *
 * On play:
 * - Controls are shown on play and hidden after 3 seconds
 * - Desktop: Controls are shown on mouse hover, hidden after 3 seconds (after mouse stops)/ 1 second (on mouse out)
 * - Mobile: Controls can be toggled visible/hidden by single tap, controls will be hidden after 3 seconds
 */

export const useControlsVisibility = (): VisibilityToggleFunctions => {
  const { getState, useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const isControlsVisible = useStoreState.isControlsVisible();
  const isPaused = useStoreState.isPaused();
  const playerUiType = useStoreState.playerUiType();
  const visibleSettingsMenu = useStoreState.visibleSettingsMenu();
  const setIsControlsVisible = useStoreState.setIsControlsVisible();

  const [controlsHover, setControlsHover] = useState(false);
  const [stickyMobileControls, setStickyMobileControls] = useState(false);

  const controlsHiddenOnStart = useRef(!getState().isControlsVisible);
  const timer = useRef<number>(undefined);

  const addTimeout = useCallback((cb: () => void, ms: number) => {
    clearInterval(timer.current);
    timer.current = setTimeout(cb, ms) as unknown as number;
  }, []);

  const toggleControls = useCallback(
    (showControls: boolean, hidingTimeout = 0) => {
      // If controls are hidden on start, do not show them on toggleControls function, because they might show unnecessarily on controls focus
      // Currently this can happen only in mobile, so controls can be shown using toggleMobileControls function
      if (controlsHiddenOnStart.current) return;

      clearInterval(timer.current);
      if (showControls) {
        setControlsHover(true);
      } else {
        addTimeout(() => setControlsHover(false), hidingTimeout);
      }
    },
    [addTimeout]
  );

  const toggleMobileControls = useCallback(
    (
      showControls?: boolean,
      hidingTimeout = CONTROL_VISIBILITY_TIMEOUTS.MOVE_HOVER
    ) => {
      clearInterval(timer.current);
      const show = showControls ?? !getState().isControlsVisible;
      setControlsHover(show);
      setStickyMobileControls(show);

      if (!isPaused) {
        addTimeout(() => {
          setControlsHover(false);
          setStickyMobileControls(false);
        }, hidingTimeout);
      }
    },
    [addTimeout, getState, isPaused]
  );

  useEffect(() => {
    if (isControlsVisible && controlsHiddenOnStart.current) {
      controlsHiddenOnStart.current = false;
    }
  }, [isControlsVisible]);

  useEffect(() => {
    if (device.isMobile) {
      if (!isPaused && getState().isControlsVisible) {
        toggleControls(true);
        toggleControls(false, CONTROL_VISIBILITY_TIMEOUTS.MOVE_HOVER);
      }
      setStickyMobileControls(isPaused);
    }
  }, [device, getState, isPaused, toggleControls]);

  useEffect(() => {
    const isPausedOnDesktop = !device.isMobile && isPaused;
    setIsControlsVisible(
      playerUiType === Enums.PlayerUiType.default &&
        (controlsHover ||
          isPausedOnDesktop ||
          stickyMobileControls ||
          !!visibleSettingsMenu)
    );
  }, [
    controlsHover,
    device,
    isPaused,
    playerUiType,
    setIsControlsVisible,
    stickyMobileControls,
    visibleSettingsMenu
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(timer.current);
    };
  }, []);

  return {
    toggleControls,
    toggleMobileControls
  };
};
